import { safeDefine } from '@breuni-ui/safe-define';
import { mediaQuery } from '../../scripts/lib/mediaQuery.js';

interface GiftCard {
  id: string;
  name: string;
  nameHtmlEncoded: string;
  vendorId: string;
  image: string;
  imageAnimated: string;
  imageAnimatedFallback: string;
}

class GiftcardSelectionElement extends HTMLElement {
  private bigImage: HTMLElement | null = null;

  private bigImageFallback: HTMLElement | null = null;

  private video: HTMLVideoElement | null | undefined = null;

  private cards: NodeListOf<Element> | null = null;

  private valueChoices: NodeListOf<Element> | null = null;

  private name: HTMLElement | null = null;

  private idInput: HTMLInputElement | null = null;

  private valueInput: HTMLInputElement | null = null;

  private bdkWrapper: HTMLElement | null = null;

  private selectedCard!: GiftCard;

  private previewCard: GiftCard | null = null;

  private errorLabel: HTMLElement | null = null;

  private ctaAriaLabel: HTMLElement | null = null;
  private valueEmptyMsg = '';
  private ariaLabelTemplate = '';

  private currentValue: number | null = null;

  private boundStopPreviewCard = this.stopPreviewCard.bind(this);

  connectedCallback() {
    this.bigImage = this.querySelector('.ents-giftcard-selection__big-image');
    this.bigImageFallback = this.querySelector('.ents-giftcard-selection__big-image--fallback');
    this.video = this.bigImage?.querySelector('video');
    this.cards = this.querySelectorAll('.ents-giftcard-selection__card-selection__list label');
    this.valueChoices = this.querySelectorAll('input[type=radio][name="value-choice"]');
    this.name = this.querySelector('#ents-giftcard-name');
    this.idInput = this.querySelector('#ents-giftcard-id');
    this.valueInput = this.querySelector('#ents-giftcard-value');
    this.bdkWrapper = this.querySelector('.ents-giftcard-selection__card-selection__card-value__bdk-wrapper');
    this.errorLabel = this.querySelector('.ents-giftcard-selection__card-selection__card-value__label');

    this.ctaAriaLabel = this.querySelector('#ents-giftcard-selection-cta-aria-label');
    this.valueEmptyMsg = this.ctaAriaLabel?.dataset?.valueEmptyMsg ?? '';
    this.ariaLabelTemplate = this.ctaAriaLabel?.dataset?.msg ?? '{0} {1}';

    this.playVideo();

    const selectedElement = this.querySelector(
      '.ents-giftcard-selection__card-selection__list label.selected',
    ) as HTMLElement;
    this.selectedCard = JSON.parse(selectedElement.dataset.giftCard || '{}');

    this.bigImage?.addEventListener('click', this.playVideo.bind(this));

    this.cards.forEach((card) => {
      card.addEventListener('change', this.onCardSelectionChange.bind(this));
      card.addEventListener('mouseenter', this.onCardMouseEnter.bind(this));
      card.addEventListener('mouseleave', this.onCardMouseLeave.bind(this));
    });

    this.valueChoices.forEach((choice) => {
      if (choice.getAttribute('checked') !== null) {
        this.updateCurrentValueState(choice as HTMLElement);
      }
      choice.addEventListener('click', this.onValueChoiceClickedOrFocussed.bind(this));
      choice.addEventListener('focus', this.onValueChoiceClickedOrFocussed.bind(this));
    });

    this.valueInput?.addEventListener('change', this.validateValueInput.bind(this));
    this.querySelector('form')?.addEventListener('submit', this.onSubmit.bind(this));

    this.updateCtaAriaLabel();

    const body = document.getElementsByTagName('body')[0];
    body.classList.add('bewerten-body--ready');

    window.addEventListener('keydown', this.boundStopPreviewCard);
  }

  disconnectedCallback() {
    window.removeEventListener('keydown', this.boundStopPreviewCard);
  }

  private stopPreviewCard(event: KeyboardEvent) {
    if (event.key === 'Escape') {
      this.unsetPreviewCard();
    }
  }

  private updateCurrentValueState(choice: HTMLElement) {
    const chosenValue = choice.getAttribute('value');
    if (!chosenValue) return;

    this.valueChoices?.forEach((input) => {
      if (!(input instanceof HTMLInputElement)) return;
      const label = this.querySelector(`label[for="${input.id}"]`);
      label?.removeAttribute('active');
    });

    if (choice instanceof HTMLInputElement) {
      const activeLabel = this.querySelector(`label[for="${choice.id}"]`);
      activeLabel?.setAttribute('active', '');
    }

    this.toggleValueInputVisible(chosenValue === '-1');
    this.currentValue = parseInt(chosenValue);
    this.updateCtaAriaLabel();
  }

  private onValueChoiceClickedOrFocussed(event: Event) {
    if (!event.target) return;
    const choice = event.target as HTMLInputElement;
    if (choice.type === 'radio') choice.checked = true;
    this.updateCurrentValueState(choice);
    if (choice.getAttribute('id') != 'value-choice-custom') {
      this.validateValueInput();
    }
  }

  private toggleValueInputVisible(show: boolean) {
    if (!this.bdkWrapper) {
      console.warn('BDK wrapper could not be located');
      return;
    }
    if (this.bdkWrapper.hidden == !show) return;
    this.bdkWrapper.hidden = !show;
    if (this.valueInput) {
      this.valueInput.value = '';
    }
    this.hideErrors();
    if (show && this.valueInput) {
      this.valueInput.focus();
    }
  }

  private onCardMouseEnter(event: Event) {
    const target = event.target as HTMLElement;
    const giftCard = JSON.parse(target.dataset.giftCard || '{}') as GiftCard;
    if (giftCard.id !== this.selectedCard.id) {
      this.setPreviewCard(giftCard);
    }
  }

  private onCardMouseLeave() {
    this.unsetPreviewCard();
  }

  private onCardSelectionChange(event: Event) {
    const target = (event.target as HTMLElement).closest('label');
    if (!target) return;
    const giftCard = JSON.parse(target.dataset.giftCard || '{}') as GiftCard;
    this.selectCard(giftCard);
    this.playVideo();
    this.updateCtaAriaLabel();
  }

  private onSubmit(event: SubmitEvent) {
    const validationResult = this.validateValueInput();
    if (!validationResult) {
      event.preventDefault();
      return;
    }
    if (this.bdkWrapper?.hidden && this.currentValue && this.valueInput) {
      this.valueInput.value = this.currentValue.toString();
    }
  }

  private selectCard(giftCard: GiftCard) {
    this.cards?.forEach((card) => card.classList.remove('selected'));
    this.cards?.forEach((card) => {
      if (card.id === giftCard.vendorId) {
        card.classList.add('selected');
      }
    });
    this.selectedCard = giftCard;
    this.unsetPreviewCard();
    this.updateVendorId();
  }

  private updateName() {
    const giftCard = this.previewCard || this.selectedCard;
    GiftcardSelectionElement.updateElementIfExists(this.name, (el) => {
      el.innerHTML = giftCard.nameHtmlEncoded;
    });
  }

  private updateVendorId() {
    GiftcardSelectionElement.updateElementIfExists(this.idInput, (el) => {
      el.value = this.selectedCard.id;
    });
  }

  private updateCtaAriaLabel() {
    GiftcardSelectionElement.updateElementIfExists(this.ctaAriaLabel, (el) => {
      const value = this.extractValue();
      const valueMsg = value ?? this.valueEmptyMsg;
      el.textContent = this.ariaLabelTemplate.replace('{0}', this.selectedCard.name).replace('{1}', valueMsg);
    });
  }

  private extractValue() {
    if (this.currentValue && this.currentValue > 0) {
      return `${this.currentValue}`;
    }
    if (!this.bdkWrapper?.hidden && this.valueInput?.value) {
      return this.valueInput?.value;
    }
    return null;
  }

  private static updateElementIfExists<ELEMENT>(element: ELEMENT | null | undefined, callback: (el: ELEMENT) => void) {
    if (element) {
      callback(element);
    }
  }

  private setPreviewCard(giftCard: GiftCard) {
    this.previewCard = giftCard;
    this.updateBigImage();
    this.updateName();
  }

  private unsetPreviewCard() {
    this.previewCard = null;
    this.updateBigImage();
    this.updateName();
  }

  private updateBigImage() {
    const giftCard = this.previewCard ?? this.selectedCard;
    const prefix = this.bigImage?.dataset.prefix ?? '';
    const altPrefix = this.bigImage?.dataset.altPrefix ?? '';

    this.bigImageFallback?.setAttribute('src', `${prefix}${giftCard.image}`);
    this.bigImageFallback?.setAttribute('alt', `${altPrefix} ${giftCard.name}`);

    this.video?.setAttribute('aria-label', giftCard.name);
    this.bigImage?.querySelectorAll('source')?.forEach((source) => {
      const sourcePrefix = source.dataset.prefix || '';
      source.setAttribute('src', `${sourcePrefix}${giftCard.imageAnimatedFallback}`);
    });

    if (this.video?.poster !== `${prefix}${giftCard.image}`) {
      this.video?.setAttribute('poster', `${prefix}${giftCard.image}`);
      this.video?.load();
    }

    if (giftCard.id !== this.selectedCard.id) {
      this.playVideo();
    }
  }

  private validateValueInput(): boolean {
    if (!this.valueInput) return false;
    let value: number;
    // Use custom input value
    if (!this.bdkWrapper?.hidden) {
      const inputValue = this.valueInput.value;
      if (inputValue.trim() === '') {
        this.showError('empty');
        return false;
      }
      if (!inputValue.trim().match(/^[0-9]+$/)) {
        this.showError('invalid');
        return false;
      }
      value = parseInt(inputValue, 10);
    } else {
      if (!this.currentValue) {
        this.showError('choice');
        return false;
      }
      value = this.currentValue;
    }
    this.updateCtaAriaLabel();
    const min = parseInt(this.valueInput.dataset.min || '', 10);
    const max = parseInt(this.valueInput.dataset.max || '', 10);
    if (value < min) {
      this.showError('too-less');
    } else if (value > max) {
      this.showError('too-much');
    } else {
      this.hideErrors();
      return true;
    }
    return false;
  }

  private showError(type: 'empty' | 'invalid' | 'too-less' | 'too-much' | 'choice') {
    this.hideErrors();

    const errorElement = this.querySelector(`#ents-giftcard-error-${type}`);
    const errorContainer = this.querySelector('#ents-giftcard-value-error-msg');
    if (errorElement && errorContainer) {
      errorContainer.innerHTML = errorElement.innerHTML;
      errorContainer.parentElement?.setAttribute('invalid', '');
    }

    this.errorLabel?.classList.add('error');
    this.valueInput?.setAttribute('aria-invalid', 'true');
    this.valueInput?.setCustomValidity(' ');
  }

  private hideErrors() {
    const errorContainer = this.querySelector('#ents-giftcard-value-error-msg');
    if (errorContainer) {
      errorContainer.innerHTML = '';
      errorContainer.parentElement?.removeAttribute('invalid');
    }
    this.errorLabel?.classList.remove('error');
    this.valueInput?.setAttribute('aria-invalid', 'false');
    this.valueInput?.setCustomValidity('');
  }

  private playVideo() {
    if (mediaQuery.prefersReducedMotion()) {
      return;
    }

    if (
      this.video != null &&
      this.video.currentTime > 0 &&
      !this.video.paused &&
      !this.video.ended &&
      this.video.readyState > 2
    ) {
      return;
    }

    const videoPlay = () => {
      this.video?.play();
      this.video?.removeEventListener('canplaythrough', videoPlay);
    };
    this.video?.addEventListener('canplaythrough', videoPlay);
    this.video?.load();
  }
}

safeDefine('ents-giftcard-selection', GiftcardSelectionElement);
